import { AgentsViewContainer } from '../components/agents/AgentsViewContainer';
import { SandboxRuntimeDetailView } from '../components/sandbox-runtimes/SandboxRuntimeDetailView';
import { SandboxRuntimesView } from '../components/sandbox-runtimes/SandboxRuntimesView';
import { SandboxFleetView } from '../components/sandbox-fleet/SandboxFleetView';
import { PoolDetailView } from '../components/sandbox-fleet/PoolDetailView';
import { SdkGovernanceView } from '../components/sdk-governance/SdkGovernanceView';
import { ApiKeysView } from '../components/api-keys/ApiKeysView';
import { ConversationDetailView, ConversationsView } from '../components/conversations/ConversationsView';
import { PlansView } from '../components/plans/PlansView';
import { GitCredentialsView } from '../components/git-credentials/GitCredentialsView';
import { ProxyVaultView } from '../components/proxy-vault/ProxyVaultView';
import { ConnectionView } from '../components/proxy-vault/ConnectionView';
import { SkillsView } from '../components/skills/SkillsView';
import { SkillsRepositoryView } from '../components/skills-repository/SkillsRepositoryView';
import { SftpView } from '../components/sftp/SftpView';
import { TeamView } from '../components/team/TeamView';
import { RolesView } from '../components/team/RolesView';
import { TenantsView } from '../components/tenants/TenantsView';

import type { OpenTabFn } from './types';

export interface ViewRenderProps {
  payload: Record<string, unknown> | undefined;
  openTab: OpenTabFn;
}

export interface ViewMeta {
  title: string;
  icon?: string;
  render: (props: ViewRenderProps) => React.ReactNode;
}

const registry: Record<string, ViewMeta> = {
  start: {
    title: 'Start',
    icon: '·',
    render: () => <StartView />,
  },
  conversations: {
    title: 'Conversations',
    icon: '💬',
    render: ({ payload, openTab }) => (
      <ConversationsView
        conversationId={(payload?.conversationId as string | undefined) ?? undefined}
        openTab={openTab}
      />
    ),
  },
  'conversation-detail': {
    title: 'Conversation',
    icon: '💬',
    render: ({ payload }) => (
      <ConversationDetailView
        conversationId={(payload?.conversationId as string | undefined) ?? undefined}
      />
    ),
  },
  tenants: {
    title: 'Organization',
    icon: '🏢',
    render: () => <TenantsView />,
  },
  users: {
    title: 'Team',
    icon: '👥',
    render: () => <TeamView />,
  },
  roles: {
    title: 'Roles',
    icon: '🛂',
    render: () => <RolesView />,
  },
  'proxy-vault': {
    title: 'Proxy Vault',
    icon: '⚿',
    render: ({ openTab }) => <ProxyVaultView openTab={openTab} />,
  },
  // A connection opens as its OWN top-level tab rather than a drawer: it has
  // four pages of its own (status, document, values, traffic) and a popover can
  // hold none of them.
  'proxy-vault-connection': {
    title: 'Connection',
    icon: '⚿',
    render: ({ payload }) => (
      <ConnectionView
        connectionId={(payload?.connectionId as string | undefined) ?? undefined}
        tenantId={(payload?.tenantId as string | undefined) ?? undefined}
      />
    ),
  },
  // The tokens the platform CLONES with — a different lane from the Proxy
  // Vault, which substitutes credentials on traffic leaving a sandbox.
  'git-credentials': {
    title: 'Git Credentials',
    icon: '🌿',
    render: () => <GitCredentialsView />,
  },
  'api-keys': {
    title: 'API Keys',
    icon: '🗝️',
    render: () => <ApiKeysView />,
  },
  plans: {
    title: 'Plans',
    icon: '📋',
    render: () => <PlansView />,
  },
  skills: {
    title: 'Skills',
    icon: '🧩',
    render: () => <SkillsView />,
  },
  'skills-repository': {
    title: 'Skills Repository',
    icon: '📦',
    render: () => <SkillsRepositoryView />,
  },
  sftp: {
    title: 'SFTP',
    icon: '🗂️',
    render: () => <SftpView />,
  },
  agents: {
    title: 'Agents',
    icon: '🤖',
    render: () => <AgentsViewContainer />,
  },
  'sandbox-runtime-logs': {
    title: 'Sandbox Runtime Logs',
    icon: '📡',
    render: ({ openTab }) => <SandboxRuntimesView openTab={openTab} />,
  },
  'sandbox-runtime-detail': {
    title: 'Sandbox Runtime',
    icon: '📡',
    render: ({ payload }) => (
      <SandboxRuntimeDetailView
        podSessionId={(payload?.podSessionId as string | undefined) ?? undefined}
        tenantId={(payload?.tenantId as string | undefined) ?? undefined}
      />
    ),
  },
  'sandbox-fleet': {
    title: 'Sandbox Fleet',
    icon: '🛳️',
    render: ({ openTab }) => <SandboxFleetView openTab={openTab} />,
  },
  'sandbox-fleet-pool': {
    title: 'Sandbox Pool',
    icon: '🛳️',
    render: ({ payload }) => <PoolDetailView poolId={(payload?.poolId as string | undefined) ?? ''} />,
  },
  'sdk-governance': {
    title: 'SDKs',
    icon: '📦',
    render: () => <SdkGovernanceView />,
  },
};

function StartView() {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        padding: 32,
        color: 'var(--text-soft)',
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: 12,
          background: 'var(--accent)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          fontWeight: 700,
        }}
      >
        S
      </div>
      <div style={{ fontSize: 18, color: 'var(--text)', fontWeight: 500 }}>
        ShiftAgent Workspace
      </div>
      <div style={{ fontSize: 13, maxWidth: 420, textAlign: 'center' }}>
        Shell scaffold. Pick a nav item to open a view, press{' '}
        <kbd
          style={{
            fontFamily: 'ui-monospace, monospace',
            background: 'var(--bg-alt2)',
            padding: '1px 6px',
            borderRadius: 4,
            border: '1px solid var(--border)',
          }}
        >
          ⌘K
        </kbd>{' '}
        to search, double-click a tab to pin it, or drag a tab to a pane edge to split.
      </div>
    </div>
  );
}

export function getViewMeta(viewId: string): ViewMeta | undefined {
  return registry[viewId];
}

export function registerView(viewId: string, meta: ViewMeta): void {
  registry[viewId] = meta;
}
